import React from 'react';
import {
    Box, Button, Chip, Dialog, DialogContent, DialogTitle,
    IconButton, Slide, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { DataAPIContext, GET_LIST } from '../api';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';

import { BackgroundTaskContext } from './BackgroundTaskProvider';
import TaskDetailPanel from './TaskDetailPanel';
import TaskProgressBar from './TaskProgressBar';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const STATE_COLORS = {
    0: 'default',
    1: 'primary',
    2: 'success',
    3: 'error',
    4: 'warning',
    5: 'info',
};

const TaskFullScreenDialog = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const { dialogOpen, setDialogOpen, expandTaskId, setExpandTaskId } = React.useContext(BackgroundTaskContext);
    const { isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const [rows, setRows] = React.useState([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [loading, setLoading] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = React.useState([]);

    const fetchTasks = React.useCallback(() => {
        if (!dialogOpen) return;
        setLoading(true);
        dataAPI(GET_LIST, 'bg_tasks', {
            pagination: { page: page + 1, perPage: pageSize },
            sort: { field: 'created_ts', order: 'DESC' },
        }).then((res) => {
            if (res && res.statusCode === 200 && res.data) {
                setRows(res.data.results || res.data || []);
                setRowCount(res.data.count || 0);
            }
        }).catch(() => {})
        .finally(() => setLoading(false));
    }, [dataAPI, dialogOpen, page, pageSize]);

    React.useEffect(() => {
        fetchTasks();
    }, [fetchTasks, refreshKey]);

    // Expand the target row once rows are loaded
    React.useEffect(() => {
        if (expandTaskId && rows.length > 0) {
            setDetailPanelExpandedRowIds([expandTaskId]);
            setExpandTaskId(null);
        }
    }, [expandTaskId, rows, setExpandTaskId]);

    // Auto-refresh every 10s while dialog is open
    React.useEffect(() => {
        if (!dialogOpen) return;
        const interval = setInterval(() => setRefreshKey(k => k + 1), 10000);
        return () => clearInterval(interval);
    }, [dialogOpen]);

    const columns = [
        {
            field: 'task_label',
            headerName: 'Operation',
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <Typography variant="body2" noWrap>
                    {params.value || params.row.task_type}
                </Typography>
            ),
        },
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee, { flex: 1, minWidth: 140 }),
        {
            field: 'state_display',
            headerName: 'Status',
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={STATE_COLORS[params.row.state] || 'default'}
                    sx={{ height: 22 }}
                />
            ),
        },
        {
            field: 'progress',
            headerName: 'Progress',
            width: 160,
            sortable: false,
            renderCell: (params) =>
                params.row.state === 1 && params.value
                    ? <TaskProgressBar progress={params.value} />
                    : null,
        },
        {
            field: 'user_email',
            headerName: 'Started by',
            width: 180,
            renderCell: (params) => (
                <Typography variant="body2" noWrap>{params.value || '-'}</Typography>
            ),
        },
        {
            field: 'created_ts',
            headerName: 'Started',
            width: 160,
            renderCell: (params) =>
                params.value ? moment(params.value).format('MMM D, HH:mm:ss') : '-',
        },
        {
            field: 'duration_seconds',
            headerName: 'Duration',
            width: 100,
            renderCell: (params) =>
                params.value != null ? `${params.value.toFixed(1)}s` : '...',
        },
    ];

    const getDetailPanelContent = React.useCallback(
        ({ row }) => <TaskDetailPanel task={row} onRefresh={() => setRefreshKey(k => k + 1)} />,
        [],
    );

    return (
        <Dialog
            fullScreen
            open={dialogOpen}
            onClose={() => { setDialogOpen(false); setDetailPanelExpandedRowIds([]); }}
            TransitionComponent={Transition}
            sx={{ zIndex: 10003 }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="h6">Background Operations</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="small" onClick={() => setRefreshKey(k => k + 1)}>
                        Refresh
                    </Button>
                    <IconButton onClick={() => setDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ height: 'calc(100vh - 80px)', width: '100%' }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        rowCount={rowCount}
                        loading={loading}
                        pagination
                        paginationMode="server"
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        rowsPerPageOptions={[10, 25, 50]}
                        getRowId={(row) => row.id}
                        disableSelectionOnClick
                        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                        onDetailPanelExpandedRowIdsChange={setDetailPanelExpandedRowIds}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={() => 'auto'}
                        density="compact"
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': { fontSize: '0.8rem' },
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TaskFullScreenDialog;
